/**
 * Core game logic without rendering
 * Used by both Game.js (browser) and training (Node.js)
 * 
 * Phase 2: Ship movement only (minimal viable core)
 */

import { GameConstants } from './GameConstants.js';
import { GameCoreEvents, GameEvents } from './GameCoreEvents.js';

export class GameCore {
    constructor(config = {}) {
        // World dimensions (calculated from camera FOV)
        this.worldWidth = config.worldWidth || GameConstants.WORLD_WIDTH_DEFAULT;
        this.worldHeight = config.worldHeight || GameConstants.WORLD_HEIGHT_DEFAULT;
        this.maxX = this.worldWidth / 2;
        this.maxY = this.worldHeight / 2;
        
        // Configuration
        this.maxShipHealth = config.maxShipHealth || GameConstants.SHIP_MAX_HEALTH;
        this.maxOre = config.maxOre || GameConstants.MAX_ORE_CAPACITY;
        
        // Events
        this.events = new GameCoreEvents();
        
        // Game state
        this.ship = null;
        this.asteroids = [];
        this.bullets = [];
        this.dropItems = [];
        
        // Game stats
        this.score = 0;
        this.ore = 0;
        this.totalOreCollected = 0;
        this.steps = 0;
        
        // Input state
        this.input = {
            left: false,
            right: false,
            up: false,
            down: false,
            space: false,
            q: false,
            e: false
        };
        
        // Game status
        this.shipOnPlatform = false;
        this.gameOverFlag = false;
        
        // ID counters for tracking entities
        this.nextAsteroidId = 1;
        this.nextBulletId = 1;
        this.nextCollectibleId = 1;
        
    }
    
    init() {
        console.log('[GameCore] init() called');
        // Initialize ship at center
        this.ship = {
            pos: { x: 0, y: 0 },  // World center
            vel: { x: 0, y: 0 },
            angle: 0,  // Radians, 0 = facing up (negative Y direction)
            speed: 0,  // Current speed input
            health: this.maxShipHealth,
            maxHealth: this.maxShipHealth,
            // Collision box (matches OBB from original game)
            collisionBox: {
                halfWidth: GameConstants.SHIP_COLLISION_BOX.halfWidth,
                halfLength: GameConstants.SHIP_COLLISION_BOX.halfLength
            },
            bulletCooldown: 0,
            idle: false
        };
        
        // Spawn initial asteroids
        for (let i = 0; i < GameConstants.ASTEROID_MIN_COUNT; i++) {
            this.spawnAsteroid('b');
        }
        console.log(`[GameCore] init() complete, spawned ${this.asteroids.length} asteroids`);
    }
    
    reset() {
        this.ship = null;
        this.asteroids = [];
        this.bullets = [];
        this.dropItems = [];
        this.score = 0;
        this.ore = 0;
        this.totalOreCollected = 0;
        this.steps = 0;
        this.gameOverFlag = false;
        this.shipOnPlatform = false;
        this.init();
    }
    
    update() {
        this.steps++;
        
        if (this.gameOverFlag) {
            return;
        }
        
        // Everything keeps running - ship can move while on platform
        this.updateShip();
        this.updateAsteroids();
        this.updateBullets();
        this.updateDropItems();
        this.checkCollisions();
    }
    
    updateShip() {
        if (this.ship.idle) return;
        
        // === CONSOLIDATED SHIP PHYSICS ===
        // This combines logic from updateShip(), renderShip(), and Ship.update()
        
        // 1. Rotation (from Game.js updateShip lines 573-574)
        // NOTE: In original, left increases angle, right decreases angle
        // NEW: Support variable rotation intensity (0.0 to 1.0) from mobile controls
        
        // Check if we have rotation intensity from mobile controls
        if (this.input.rotationIntensity !== undefined && this.input.rotationIntensity > 0) {
            // Mobile: Use variable speed rotation based on slider position
            // rotationDirection: 1 = rotate left (increase angle), -1 = rotate right (decrease angle)
            // rotationIntensity: 0.0 to 1.0 (percentage of max speed)
            const rotationAmount = GameConstants.SHIP_ROTATION_SPEED * this.input.rotationIntensity;
            this.ship.angle += rotationAmount * this.input.rotationDirection;
        } else {
            // Desktop: Binary keyboard controls (full speed or no speed)
            if (this.input.left) {
                this.ship.angle += GameConstants.SHIP_ROTATION_SPEED;
            }
            if (this.input.right) {
                this.ship.angle -= GameConstants.SHIP_ROTATION_SPEED;
            }
        }
        
        // 2. Speed input (from Game.js updateShip lines 576-588)
        if (this.input.up) {
            this.ship.speed += GameConstants.SHIP_ACCELERATION;
        }
        
        if (this.input.down) {
            this.ship.speed = 0;
            this.ship.vel.x *= GameConstants.SHIP_FRICTION_BRAKE;
            this.ship.vel.y *= GameConstants.SHIP_FRICTION_BRAKE;
        }
        
        if (!this.input.up && !this.input.down) {
            this.ship.speed = 0;
            this.ship.vel.x *= GameConstants.SHIP_FRICTION_NORMAL;
            this.ship.vel.y *= GameConstants.SHIP_FRICTION_NORMAL;
        }
        
        // 3. Clamp speed (from Game.js updateShip line 600)
        this.ship.speed = Math.max(0, Math.min(GameConstants.SHIP_MAX_SPEED, this.ship.speed));
        
        // 4. Apply forward acceleration (from Game.js renderShip lines 621-624)
        // This is the physics that was in the render function!
        // Vector3(0, 0, -1) means forward in THREE.js (negative Z)
        // In our 2D system, forward is negative Y
        if (this.ship.speed > 0) {
            const forwardAccel = GameConstants.SHIP_FORWARD_ACCEL * this.ship.speed;
            // Forward direction: -sin(angle) for X, -cos(angle) for Y
            // Negated X because THREE.js rotation.y convention is opposite
            this.ship.vel.x += -Math.sin(this.ship.angle) * forwardAccel;
            this.ship.vel.y += -Math.cos(this.ship.angle) * forwardAccel;
        }
        
        // 5. Strafe (from Game.js updateShip lines 590-598)
        // Using angle-based math instead of quaternions
        // Vector3(1, 0, 0) rotated by quaternion = strafe right
        // With negated X component, strafe right is angle - PI/2, strafe left is angle + PI/2
        if (this.input.e) {
            // Strafe right
            const strafeAngle = this.ship.angle - Math.PI / 2;
            this.ship.vel.x += -Math.sin(strafeAngle) * GameConstants.SHIP_STRAFE_ACCEL;
            this.ship.vel.y += -Math.cos(strafeAngle) * GameConstants.SHIP_STRAFE_ACCEL;
        }
        
        if (this.input.q) {
            // Strafe left
            const strafeAngle = this.ship.angle + Math.PI / 2;
            this.ship.vel.x += -Math.sin(strafeAngle) * GameConstants.SHIP_STRAFE_ACCEL;
            this.ship.vel.y += -Math.cos(strafeAngle) * GameConstants.SHIP_STRAFE_ACCEL;
        }
        
        // 6. Velocity clamping (from Game.js renderShip lines 626-628)
        const velMagnitude = Math.sqrt(this.ship.vel.x ** 2 + this.ship.vel.y ** 2);
        if (velMagnitude > GameConstants.SHIP_VEL_MAX_MAGNITUDE) {
            this.ship.vel.x = (this.ship.vel.x / velMagnitude) * GameConstants.SHIP_VEL_MAX_MAGNITUDE;
            this.ship.vel.y = (this.ship.vel.y / velMagnitude) * GameConstants.SHIP_VEL_MAX_MAGNITUDE;
        }
        
        // 7. Update position (from Ship.js update line 83)
        const posBeforeWrap = { x: this.ship.pos.x, y: this.ship.pos.y };
        this.ship.pos.x += this.ship.vel.x;
        this.ship.pos.y += this.ship.vel.y;
        
        // 8. Screen wrapping (FIXED: Use loop to handle multiple wraps in one frame)
        // IMPORTANT: Original multiplies position by -1, not set to -maxPos!
        // Example: if at position 26, wrap to -26 (not -25)
        // CRITICAL FIX: The original if/else-if only wrapped once, allowing escape if
        // the wrapped position was still out of bounds. Now we loop until within bounds.
        let wrappedX = false;
        let wrappedY = false;
        const posBeforeWrapCheck = { x: this.ship.pos.x, y: this.ship.pos.y };
        let wrapCountX = 0;
        let wrapCountY = 0;
        const maxWraps = 10; // Safety limit to prevent infinite loops
        
        // Wrap X axis - use proper wrapping that handles any distance beyond boundary
        // The original multiply-by-1 logic only works for small overflows. For large overflows,
        // we need to properly wrap to the opposite side while maintaining the offset.
        const oldX = this.ship.pos.x;
        if (this.ship.pos.x > this.maxX || this.ship.pos.x < -this.maxX) {
            wrappedX = true;
            // Calculate how far beyond the boundary we are
            let overflow = 0;
            if (this.ship.pos.x > this.maxX) {
                overflow = this.ship.pos.x - this.maxX;
                // Wrap to opposite side: start at -maxX and add the overflow
                this.ship.pos.x = -this.maxX + overflow;
                // If still out of bounds, wrap again (handles cases where overflow > 2*maxX)
                while (this.ship.pos.x > this.maxX && wrapCountX < maxWraps) {
                    overflow = this.ship.pos.x - this.maxX;
                    this.ship.pos.x = -this.maxX + overflow;
                    wrapCountX++;
                }
            } else if (this.ship.pos.x < -this.maxX) {
                overflow = -this.maxX - this.ship.pos.x;
                // Wrap to opposite side: start at maxX and subtract the overflow
                this.ship.pos.x = this.maxX - overflow;
                // If still out of bounds, wrap again
                while (this.ship.pos.x < -this.maxX && wrapCountX < maxWraps) {
                    overflow = -this.maxX - this.ship.pos.x;
                    this.ship.pos.x = this.maxX - overflow;
                    wrapCountX++;
                }
            }
            
        }
        
        // Wrap Y axis - same logic as X
        const oldY = this.ship.pos.y;
        if (this.ship.pos.y > this.maxY || this.ship.pos.y < -this.maxY) {
            wrappedY = true;
            // Calculate how far beyond the boundary we are
            let overflow = 0;
            if (this.ship.pos.y > this.maxY) {
                overflow = this.ship.pos.y - this.maxY;
                // Wrap to opposite side: start at -maxY and add the overflow
                this.ship.pos.y = -this.maxY + overflow;
                // If still out of bounds, wrap again (handles cases where overflow > 2*maxY)
                while (this.ship.pos.y > this.maxY && wrapCountY < maxWraps) {
                    overflow = this.ship.pos.y - this.maxY;
                    this.ship.pos.y = -this.maxY + overflow;
                    wrapCountY++;
                }
            } else if (this.ship.pos.y < -this.maxY) {
                overflow = -this.maxY - this.ship.pos.y;
                // Wrap to opposite side: start at maxY and subtract the overflow
                this.ship.pos.y = this.maxY - overflow;
                // If still out of bounds, wrap again
                while (this.ship.pos.y < -this.maxY && wrapCountY < maxWraps) {
                    overflow = -this.maxY - this.ship.pos.y;
                    this.ship.pos.y = this.maxY - overflow;
                    wrapCountY++;
                }
            }
            
        }
        
        // Final safety check: if still out of bounds after all wraps, clamp to boundary
        const stillOutOfBoundsX = Math.abs(this.ship.pos.x) > this.maxX;
        const stillOutOfBoundsY = Math.abs(this.ship.pos.y) > this.maxY;
        if (stillOutOfBoundsX || stillOutOfBoundsY) {
            console.error('[BOUNDARY_EXPLOIT_DETECTED] Ship still out of bounds after wrapping! Clamping...', {
                positionBeforeClamp: { x: this.ship.pos.x.toFixed(4), y: this.ship.pos.y.toFixed(4) },
                posBeforeWrap: { x: posBeforeWrap.x.toFixed(4), y: posBeforeWrap.y.toFixed(4) },
                posBeforeWrapCheck: { x: posBeforeWrapCheck.x.toFixed(4), y: posBeforeWrapCheck.y.toFixed(4) },
                velocity: { x: this.ship.vel.x.toFixed(6), y: this.ship.vel.y.toFixed(6) },
                boundaries: { maxX: this.maxX.toFixed(4), maxY: this.maxY.toFixed(4) },
                wrappedX: wrappedX,
                wrappedY: wrappedY,
                wrapCountX: wrapCountX,
                wrapCountY: wrapCountY,
                stillOutOfBoundsX: stillOutOfBoundsX,
                stillOutOfBoundsY: stillOutOfBoundsY,
                step: this.steps,
                input: { ...this.input }
            });
            
            // Clamp to boundary as emergency fix
            if (this.ship.pos.x > this.maxX) {
                this.ship.pos.x = this.maxX;
            } else if (this.ship.pos.x < -this.maxX) {
                this.ship.pos.x = -this.maxX;
            }
            if (this.ship.pos.y > this.maxY) {
                this.ship.pos.y = this.maxY;
            } else if (this.ship.pos.y < -this.maxY) {
                this.ship.pos.y = -this.maxY;
            }
        }
        
        
        // 9. Bullet cooldown (from Ship.js update line 76)
        if (this.ship.bulletCooldown > 0) {
            this.ship.bulletCooldown--;
        }
        
        // 10. Shooting
        if (this.input.space && this.ship.bulletCooldown === 0 && !this.shipOnPlatform) {
            this.shoot();
        }
    }
    
    // === ASTEROID METHODS ===
    
    spawnAsteroid(type = 'b', pos = null) {
        const asteroidType = GameConstants.ASTEROID_TYPES[type];
        if (!asteroidType) {
            console.warn(`Unknown asteroid type: ${type}, using 'b'`);
            type = 'b';
        }
        
        // Spawn position: if pos provided, use it; otherwise spawn at corner
        // Original spawns new asteroids at (maxPos.x, maxPos.y) which is the top-right corner
        // When spawning smaller asteroids from destruction, spawn at destroyed asteroid's position
        let x, y;
        if (pos) {
            x = pos.x;
            y = pos.y;
        } else {
            // Spawn at corner (matching original Game.js line 1187)
            x = this.maxX;
            y = this.maxY;
        }
        
        // Random velocity direction (matching original Game.js line 1222)
        // Original: new THREE.Vector3(Math.random() * 2 - 1, 0, Math.random() * 2 - 1).normalize()
        // This gives completely random direction in X-Z plane (X-Y in GameCore)
        let directionX = Math.random() * 2 - 1;  // Random between -1 and 1
        let directionY = Math.random() * 2 - 1;  // Random between -1 and 1
        const magnitude = Math.sqrt(directionX ** 2 + directionY ** 2);
        
        // Normalize (avoid division by zero)
        if (magnitude > 0.001) {
            directionX /= magnitude;
            directionY /= magnitude;
        } else {
            // Fallback if random values are both near zero
            directionX = 1;
            directionY = 0;
        }
        
        // Speed calculation (from Game.js line 745)
        const speed = GameConstants.ASTEROID_SPEED_BASE + 
                      Math.random() * GameConstants.ASTEROID_SPEED_RANDOM +
                      this.score * GameConstants.ASTEROID_SPEED_SCORE_FACTOR;
        
        const asteroid = {
            id: this.nextAsteroidId++,
            pos: { x, y },
            vel: {
                x: directionX * speed,
                y: directionY * speed
            },
            radius: asteroidType.radius,
            health: asteroidType.health,
            maxHealth: asteroidType.health,
            damage: asteroidType.damage,
            score: asteroidType.score,
            type: type,
            rotation: 0,
            rotationSpeed: GameConstants.ASTEROID_ROTATION_MIN + 
                          Math.random() * GameConstants.ASTEROID_ROTATION_RANGE,
            blacklisted: false
        };
        
        this.asteroids.push(asteroid);
        
        this.events.emit(GameEvents.ASTEROID_SPAWNED, asteroid);
    }
    
    updateAsteroids() {
        // Update positions
        for (const asteroid of this.asteroids) {
            // Update position
            asteroid.pos.x += asteroid.vel.x;
            asteroid.pos.y += asteroid.vel.y;
            
            // Update rotation
            asteroid.rotation += asteroid.rotationSpeed;
            
            // Wrap around screen (matching original Game.js lines 1657-1667)
            // Original uses: pos.x = pos.x * -1 (simple flip to opposite side)
            if (asteroid.pos.x > this.maxX) {
                asteroid.pos.x = asteroid.pos.x * -1;
            } else if (asteroid.pos.x < -this.maxX) {
                asteroid.pos.x = asteroid.pos.x * -1;
            }
            
            if (asteroid.pos.y > this.maxY) {
                asteroid.pos.y = asteroid.pos.y * -1;
            } else if (asteroid.pos.y < -this.maxY) {
                asteroid.pos.y = asteroid.pos.y * -1;
            }
        }
        
        // Remove blacklisted asteroids
        this.asteroids = this.asteroids.filter(a => !a.blacklisted);
        
        // Spawn new asteroids if needed (from Game.js line 712)
        const targetCount = GameConstants.ASTEROID_MIN_COUNT + 
                           Math.floor(this.score / 1000) * 
                           GameConstants.ASTEROID_SPAWN_INCREMENT_PER_1000_SCORE;
        
        while (this.asteroids.length < targetCount) {
            this.spawnAsteroid('b');  // Always spawn big asteroids for now
        }
    }
    
    // === BULLET METHODS ===
    
    shoot() {
        this.ship.bulletCooldown = GameConstants.BULLET_COOLDOWN;
        
        // Bullet spawns at ship position (original generateShot() just copies ship.pos)
        // No offset - bullets spawn directly at ship position
        const bulletX = this.ship.pos.x;
        const bulletY = this.ship.pos.y;
        
        // Calculate bullet velocity (from renderBullets line 1092)
        // Vector3(0, 0, -speed) rotated by quaternion = forward direction
        // In our 2D system: -sin(angle) for X, -cos(angle) for Y
        // Negated X because THREE.js rotation.y convention is opposite
        const bulletSpeed = GameConstants.BULLET_SPEED;
        const bulletVelX = -Math.sin(this.ship.angle) * bulletSpeed;
        const bulletVelY = -Math.cos(this.ship.angle) * bulletSpeed;
        
        const bullet = {
            id: this.nextBulletId++,
            pos: { x: bulletX, y: bulletY },
            vel: { x: bulletVelX, y: bulletVelY },
            damage: GameConstants.BULLET_DAMAGE,
            // Bullet collision box (matches original Box3 collision)
            collisionBox: {
                halfWidth: GameConstants.BULLET_COLLISION_BOX.halfWidth,
                halfDepth: GameConstants.BULLET_COLLISION_BOX.halfDepth
            },
            blacklisted: false,
            angle: this.ship.angle  // Store for rendering and collision
        };
        
        this.bullets.push(bullet);
        
        this.events.emit(GameEvents.BULLET_FIRED, bullet);
    }
    
    updateBullets() {
        for (const bullet of this.bullets) {
            // Update position
            bullet.pos.x += bullet.vel.x;
            bullet.pos.y += bullet.vel.y;
            
            // Check if out of bounds
            const margin = 10;
            if (bullet.pos.x < -this.maxX - margin ||
                bullet.pos.x > this.maxX + margin ||
                bullet.pos.y < -this.maxY - margin ||
                bullet.pos.y > this.maxY + margin) {
                bullet.blacklisted = true;
            }
        }
        
        // Remove blacklisted bullets
        this.bullets = this.bullets.filter(b => !b.blacklisted);
    }
    
    // === COLLECTIBLE METHODS ===
    
    spawnCollectible(pos, type) {
        const collectible = {
            id: this.nextCollectibleId++,
            pos: { x: pos.x, y: pos.y },
            type: type,  // 'ore' or 'health'
            radius: GameConstants.COLLECTIBLE_RADIUS,
            blacklisted: false
        };
        
        this.dropItems.push(collectible);
        
        this.events.emit(GameEvents.COLLECTIBLE_SPAWNED, collectible);
    }
    
    updateDropItems() {
        // Drop items are stationary in the original game
        // Just remove blacklisted ones
        this.dropItems = this.dropItems.filter(d => !d.blacklisted);
    }
    
    // === COLLISION DETECTION ===
    
    checkCircleCollision(x1, y1, r1, x2, y2, r2) {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < (r1 + r2);
    }
    
    /**
     * Check collision between rotated rectangle and circle
     * This replicates the OBB (Oriented Bounding Box) collision from the original game
     * @param {Object} rectPos - Rectangle center position {x, y}
     * @param {number} rectAngle - Rectangle rotation angle in radians
     * @param {number} rectHalfWidth - Half width of rectangle
     * @param {number} rectHalfLength - Half length of rectangle
     * @param {Object} circlePos - Circle center position {x, y}
     * @param {number} circleRadius - Circle radius
     * @returns {boolean} True if collision detected
     */
    checkRectangleCircleCollision(rectPos, rectAngle, rectHalfWidth, rectHalfLength, circlePos, circleRadius) {
        // Transform circle position to rectangle's local space (inverse rotation)
        const dx = circlePos.x - rectPos.x;
        const dy = circlePos.y - rectPos.y;
        
        // Rotate by negative angle to get local coordinates
        const cos = Math.cos(-rectAngle);
        const sin = Math.sin(-rectAngle);
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        
        // Find closest point on the rectangle to the circle center
        const closestX = Math.max(-rectHalfWidth, Math.min(rectHalfWidth, localX));
        const closestY = Math.max(-rectHalfLength, Math.min(rectHalfLength, localY));
        
        // Calculate distance from circle center to closest point
        const distX = localX - closestX;
        const distY = localY - closestY;
        const distanceSquared = distX * distX + distY * distY;
        
        // THREE.js OBB.intersectsSphere requires slight overlap before collision
        // Add tolerance to match original game behavior
        const tolerance = GameConstants.SHIP_COLLISION_BOX.collisionTolerance || 0;
        const effectiveRadius = circleRadius - tolerance;
        
        // Collision if distance is less than effective radius (with tolerance)
        return distanceSquared < (effectiveRadius * effectiveRadius);
    }
    
    /**
     * Check collision between rotated box (bullet) and sphere (asteroid)
     * This replicates the Box3-Sphere collision from the original game
     * Original: asteroidSphere.intersectsBox(bulletBox3)
     * @param {Object} boxPos - Box center position {x, y}
     * @param {number} boxAngle - Box rotation angle in radians
     * @param {number} boxHalfWidth - Half width of box (X dimension)
     * @param {number} boxHalfDepth - Half depth of box (Z/Y dimension in 2D)
     * @param {Object} spherePos - Sphere center position {x, y}
     * @param {number} sphereRadius - Sphere radius
     * @returns {boolean} True if collision detected
     */
    checkBoxSphereCollision(boxPos, boxAngle, boxHalfWidth, boxHalfDepth, spherePos, sphereRadius) {
        // This is the same algorithm as Rectangle-Circle collision
        // Transform sphere position to box's local space (inverse rotation)
        const dx = spherePos.x - boxPos.x;
        const dy = spherePos.y - boxPos.y;
        
        // Rotate by negative angle to get local coordinates
        const cos = Math.cos(-boxAngle);
        const sin = Math.sin(-boxAngle);
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        
        // Find closest point on the box to the sphere center
        const closestX = Math.max(-boxHalfWidth, Math.min(boxHalfWidth, localX));
        const closestY = Math.max(-boxHalfDepth, Math.min(boxHalfDepth, localY));
        
        // Calculate distance from sphere center to closest point
        const distX = localX - closestX;
        const distY = localY - closestY;
        const distanceSquared = distX * distX + distY * distY;
        
        // Collision if distance is less than radius
        return distanceSquared < (sphereRadius * sphereRadius);
    }
    
    checkCollisions() {
        this.checkBulletAsteroidCollisions();
        
        if (!this.shipOnPlatform) {
            this.checkShipAsteroidCollisions();
        }
        
        this.checkShipCollectibleCollisions();
    }
    
    checkBulletAsteroidCollisions() {
        for (let i = this.bullets.length - 1; i >= 0; i--) {
            const bullet = this.bullets[i];
            if (bullet.blacklisted) continue;
            
            for (let j = this.asteroids.length - 1; j >= 0; j--) {
                const asteroid = this.asteroids[j];
                if (asteroid.blacklisted) continue;
                
                // Use box-sphere collision (matches original: asteroidSphere.intersectsBox(bulletBox3))
                if (this.checkBoxSphereCollision(
                    bullet.pos,
                    bullet.angle,
                    bullet.collisionBox.halfWidth,
                    bullet.collisionBox.halfDepth,
                    asteroid.pos,
                    asteroid.radius
                )) {
                    // Bullet hit asteroid
                    bullet.blacklisted = true;
                    asteroid.health -= bullet.damage;
                    
                    this.events.emit(GameEvents.ASTEROID_DAMAGED, {
                        asteroid,
                        damage: bullet.damage
                    });
                    
                    if (asteroid.health <= 0) {
                        // Asteroid destroyed
                        asteroid.blacklisted = true;
                        this.score += asteroid.score;
                        
                        // Spawn smaller asteroids (matching original Game.js resolveAsteroidType lines 1527-1537)
                        if (asteroid.type === 'b') {
                            // Big asteroid splits into 2 medium asteroids
                            this.spawnAsteroid('m', asteroid.pos);
                            this.spawnAsteroid('m', asteroid.pos);
                        } else if (asteroid.type === 'm') {
                            // Medium asteroid splits into 2 small asteroids
                            this.spawnAsteroid('s', asteroid.pos);
                            this.spawnAsteroid('s', asteroid.pos);
                        }
                        // Small asteroids and bosses don't split
                        
                        // Random boss spawns (matching original Game.js lines 1519-1524)
                        if (Math.round(Math.random() * 35) === Math.round(Math.random() * 35)) {
                            this.spawnAsteroid('boss');
                        }
                        if (Math.round(Math.random() * 45) === Math.round(Math.random() * 45)) {
                            this.spawnAsteroid('boss');
                        }
                        
                        this.events.emit(GameEvents.ASTEROID_DESTROYED, asteroid);
                        this.events.emit(GameEvents.SCORE_CHANGED, this.score);
                        
                        // Spawn collectibles (from Game.js lines 810-811)
                        // Using the same random check logic
                        if (Math.round(Math.random() * 20) === Math.round(Math.random() * 20)) {
                            this.spawnCollectible(asteroid.pos, 'ore');
                        }
                        if (Math.round(Math.random() * 45) === Math.round(Math.random() * 45)) {
                            this.spawnCollectible(asteroid.pos, 'health');
                        }
                    }
                    break;  // Bullet can only hit one asteroid
                }
            }
        }
    }
    
    checkShipAsteroidCollisions() {
        for (let i = this.asteroids.length - 1; i >= 0; i--) {
            const asteroid = this.asteroids[i];
            if (asteroid.blacklisted) continue;
            
            // Calculate OBB center position (ship pos + rotated center offset)
            // The ship mesh is offset from the ship position, need to account for rotation
            const cos = Math.cos(this.ship.angle);
            const sin = Math.sin(this.ship.angle);
            const offsetX = GameConstants.SHIP_COLLISION_BOX.centerOffsetX;
            const offsetY = GameConstants.SHIP_COLLISION_BOX.centerOffsetY;
            
            // Rotate the offset by ship's angle and add to ship position
            const obbCenterX = this.ship.pos.x + (offsetX * cos - offsetY * sin);
            const obbCenterY = this.ship.pos.y + (offsetX * sin + offsetY * cos);
            
            // Use rectangle-circle collision to replicate OBB behavior
            const collisionDetected = this.checkRectangleCircleCollision(
                { x: obbCenterX, y: obbCenterY },  // Use OBB center, not ship position
                this.ship.angle,
                this.ship.collisionBox.halfWidth,
                this.ship.collisionBox.halfLength,
                asteroid.pos,
                asteroid.radius
            );
            
            if (collisionDetected) {
                // Ship hit asteroid (matching original Game.js checkShipAsteroidCollisions)
                this.ship.health -= asteroid.damage;
                asteroid.blacklisted = true;
                
                // Spawn smaller asteroids (same logic as bullet collision)
                if (asteroid.type === 'b') {
                    this.spawnAsteroid('m', asteroid.pos);
                    this.spawnAsteroid('m', asteroid.pos);
                } else if (asteroid.type === 'm') {
                    this.spawnAsteroid('s', asteroid.pos);
                    this.spawnAsteroid('s', asteroid.pos);
                }
                
                // Random boss spawns
                if (Math.round(Math.random() * 35) === Math.round(Math.random() * 35)) {
                    this.spawnAsteroid('boss');
                }
                if (Math.round(Math.random() * 45) === Math.round(Math.random() * 45)) {
                    this.spawnAsteroid('boss');
                }
                
                // Add score (matching original - destroyAsteroid adds score)
                this.score += asteroid.score;
                this.events.emit(GameEvents.SCORE_CHANGED, this.score);
                
                // Emit asteroid destroyed event for explosion animation
                this.events.emit(GameEvents.ASTEROID_DESTROYED, asteroid);
                
                this.events.emit(GameEvents.SHIP_DAMAGED, {
                    damage: asteroid.damage,
                    health: this.ship.health
                });
                this.events.emit(GameEvents.SHIP_HEALTH_CHANGED, this.ship.health);
                
                if (this.ship.health <= 0) {
                    this.gameOverFlag = true;
                    this.events.emit(GameEvents.GAME_OVER, {
                        score: this.score,
                        reason: 'health_depleted'
                    });
                }
            }
        }
    }
    
    checkShipCollectibleCollisions() {
        for (let i = this.dropItems.length - 1; i >= 0; i--) {
            const item = this.dropItems[i];
            if (item.blacklisted) continue;
            
            // Calculate OBB center position (ship pos + rotated center offset)
            const cos = Math.cos(this.ship.angle);
            const sin = Math.sin(this.ship.angle);
            const offsetX = GameConstants.SHIP_COLLISION_BOX.centerOffsetX;
            const offsetY = GameConstants.SHIP_COLLISION_BOX.centerOffsetY;
            
            // Rotate the offset by ship's angle and add to ship position
            const obbCenterX = this.ship.pos.x + (offsetX * cos - offsetY * sin);
            const obbCenterY = this.ship.pos.y + (offsetX * sin + offsetY * cos);
            
            // Use rectangle-circle collision to replicate OBB behavior
            if (this.checkRectangleCircleCollision(
                { x: obbCenterX, y: obbCenterY },  // Use OBB center, not ship position
                this.ship.angle,
                this.ship.collisionBox.halfWidth,
                this.ship.collisionBox.halfLength,
                item.pos,
                item.radius
            )) {
                // Collected!
                item.blacklisted = true;
                
                if (item.type === 'health') {
                    if (this.ship.health < this.ship.maxHealth) {
                        this.ship.health = Math.min(
                            this.ship.maxHealth,
                            this.ship.health + GameConstants.HEALTH_PICKUP_VALUE
                        );
                        
                        this.events.emit(GameEvents.COLLECTIBLE_COLLECTED, {
                            type: 'health',
                            value: GameConstants.HEALTH_PICKUP_VALUE,
                            newHealth: this.ship.health
                        });
                        this.events.emit(GameEvents.SHIP_HEALTH_CHANGED, this.ship.health);
                    }
                } else if (item.type === 'ore') {
                    if (this.ore < this.maxOre) {
                        this.ore++;
                        this.totalOreCollected++;
                        
                        this.events.emit(GameEvents.COLLECTIBLE_COLLECTED, {
                            type: 'ore',
                            value: 1,
                            newOre: this.ore
                        });
                        this.events.emit(GameEvents.ORE_CHANGED, this.ore);
                    }
                }
            }
        }
    }
    
    // === PUBLIC API ===
    
    /**
     * Set input state
     * @param {Object} input - {left, right, up, down, space, q, e}
     */
    setInput(input) {
        this.input.left = input.left || false;
        this.input.right = input.right || false;
        this.input.up = input.up || false;
        this.input.down = input.down || false;
        this.input.space = input.space || false;
        this.input.q = input.q || false;
        this.input.e = input.e || false;
        // Mobile: Variable rotation intensity
        this.input.rotationIntensity = input.rotationIntensity !== undefined ? input.rotationIntensity : 0;
        this.input.rotationDirection = input.rotationDirection !== undefined ? input.rotationDirection : 0;
    }
    
    /**
     * Get ship state
     * @returns {Object} Ship state object
     */
    getShip() {
        return this.ship;
    }
    
    /**
     * Get asteroids array
     * @returns {Array} Array of asteroid objects
     */
    getAsteroids() {
        return this.asteroids;
    }
    
    /**
     * Get bullets array
     * @returns {Array} Array of bullet objects
     */
    getBullets() {
        return this.bullets;
    }
    
    /**
     * Get drop items array
     * @returns {Array} Array of collectible objects
     */
    getDropItems() {
        return this.dropItems;
    }
    
    /**
     * Get current score
     * @returns {number} Score
     */
    getScore() {
        return this.score;
    }
    
    /**
     * Get current ore count
     * @returns {number} Ore count
     */
    getOre() {
        return this.ore;
    }
    
    /**
     * Check if game is over
     * @returns {boolean} True if game over
     */
    isGameOver() {
        return this.gameOverFlag;
    }
    
    /**
     * Check if ship is on platform
     * @returns {boolean} True if ship on platform
     */
    isShipOnPlatform() {
        return this.shipOnPlatform;
    }
}

